import { Checkbox } from 'src/components/ui/checkbox';
import { ComboBox } from '../../../ui/comboBox/ComboBox';
import { IUser } from 'src/utils/interfaces/user';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from '../../../../../utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    formatPostalCode_CA,
    formatPostalCode_US,
} from 'src/utils/formatInputs';
import { getStateOptions, removeLeadingZeros } from '../../utils';
import { handleKeyDownYearMonth, safeString } from 'src/utils/helpers';
import { NUMBER_FIVE, NUMBER_TWO, NUMBER_ZERO } from 'src/utils/constants';
import React, { useEffect, useState } from 'react';

const ZERO = 0;
const MAXZIPCA = 7;
const MAXZIPUS = 5;
const MAX_MONTH = 11;

interface IProps {
    handlerChange: Function;
    isDisabled?: boolean;
    initialPrimaryApplicant?: any;
    isEditMode: boolean;
    setFieldValueUpdated: Function;
}

const Company = ({
    handlerChange,
    isDisabled = false,
    initialPrimaryApplicant = null,
    isEditMode = false,
    setFieldValueUpdated,
}: IProps) => {
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const { markCreditFormTouched } = useCreditForm();
    const isCanada =
        dealerDataInfo?.dealer.country === 'Canada' ||
        dealerDataInfo?.dealer.country === 'CA';
    const { occupationIndividualInformation } = useLocalization();
    const [stateOptions, setStateOptions] = useState<any[]>([]);
    const isEmploymentTypeRetired: boolean =
        initialPrimaryApplicant?.EmploymentType === 'Retired' || false;
    const [retired, setRetired] = useState(isEmploymentTypeRetired);
    const [employed, setEmployed] = useState(false);
    const [selfEmployed, setSelfEmployed] = useState(
        initialPrimaryApplicant?.EmploymentType === 'SelfEmployed',
    );
    const employer = initialPrimaryApplicant?.CurrentEmployer;
    const employerAddress = employer?.Address;

    const [company, setCompany] = useState(safeString(employer?.EmployerName));
    const [errorCompany, setErrorCompany] = useState(false);

    const [address, setAddress] = useState(
        safeString(employerAddress?.AddressLine1),
    );
    const [errorAddress, setErrorAddress] = useState(false);
    const [addressSecondary, setAddressSecondary] = useState(
        safeString(employerAddress?.AddressLine2),
    );
    const [, setErrorAddressSecondary] = useState(false);
    const [city, setCity] = useState(safeString(employerAddress?.City));
    const [errorCity, setErrorCity] = useState(false);

    const [country, setCountry] = useState(() => {
        const defaultCountry = isCanada ? 'Canada' : 'USA';
        const initialCountry =
            initialPrimaryApplicant?.CurrentEmployer?.Address?.Country;

        return initialCountry && initialCountry !== 'None'
            ? initialCountry
            : defaultCountry;
    });

    const [province, setProvince] = useState(
        initialPrimaryApplicant?.CurrentEmployer?.Address?.State ?? 'AK',
    );

    const [code, setCode] = useState(() => {
        const code =
            initialPrimaryApplicant?.CurrentEmployer?.Address?.ZipCode?.Code ??
            '';
        const codeExtension =
            initialPrimaryApplicant?.CurrentEmployer?.Address?.ZipCode
                ?.CodeExtension ?? '';
        const countryApp =
            initialPrimaryApplicant?.CurrentEmployer?.Address?.Country;

        if (countryApp === 'CA' || countryApp === 'Canada')
            return code === '' ? code : code + '-' + codeExtension;
        else {
            const zip = `${code}${codeExtension}`;
            return zip.slice(NUMBER_ZERO, NUMBER_FIVE);
        }
    });

    const [errorCode, setErrorCode] = useState(false);
    const [occupation, setOccupation] = useState(() => {
        if (isEmploymentTypeRetired) {
            return '';
        } else {
            return safeString(employer?.Occupation);
        }
    });
    const [errorOcuppation, setErrorOcuppation] = useState(false);
    const [jobYear, setJobYear] = useState(() => {
        if (isEmploymentTypeRetired) {
            return '';
        } else {
            return (
                initialPrimaryApplicant?.CurrentEmployer?.TimeAtJob?.Years ??
                null
            );
        }
    });
    const [errorJobYear, setErrorJobYear] = useState(false);
    const [jobMonth, setJobMonth] = useState(() => {
        if (isEmploymentTypeRetired) {
            return '';
        } else {
            return (
                initialPrimaryApplicant?.CurrentEmployer?.TimeAtJob?.Months ??
                null
            );
        }
    });
    const [errorJobMonth, setErrorJobMonth] = useState(false);

    // state for previous employer
    const preEmployer = initialPrimaryApplicant?.PreviousEmployer;
    const preEmployerAddress = preEmployer?.Address;
    const [preStateOptions, setPreStateOptions] = useState<any[]>([]);
    const [preCompany, setPreCompany] = useState(
        safeString(preEmployer?.EmployerName),
    );
    const [errorPreCompany, setErrorPreCompany] = useState(false);

    const [preAddress, setPreAddress] = useState(
        safeString(preEmployerAddress?.AddressLine1),
    );
    const [errorPreAddress, setErrorPreAddress] = useState(false);
    const [preAddressSecondary, setPreAddressSecondary] = useState(
        safeString(preEmployerAddress?.AddressLine2),
    );
    const [, setErrorPreAddressSecondary] = useState(false);
    const [preCity, setPreCity] = useState(
        safeString(preEmployerAddress?.City),
    );
    const [errorPreCity, setErrorPreCity] = useState(false);

    const [preCountry, setPreCountry] = useState(() => {
        const defaultCountry = isCanada ? 'Canada' : 'USA';

        return (
            initialPrimaryApplicant?.PreviousEmployer?.Address?.Country ??
            defaultCountry
        );
    });
    const [preProvince, setPreProvince] = useState('AK');
    const [preCode, setPreCode] = useState(() => {
        const code =
            initialPrimaryApplicant?.PreviousEmployer?.Address?.ZipCode?.Code ??
            '';
        const codeExtension =
            initialPrimaryApplicant?.PreviousEmployer?.Address?.ZipCode
                ?.CodeExtension ?? '';

        const countryApp =
            initialPrimaryApplicant?.PreviousEmployer?.Address?.Country;

        if (countryApp === 'CA' || countryApp === 'Canada')
            return code === '' ? code : code + '-' + codeExtension;
        else {
            const zip = `${code}${codeExtension}`;
            return zip.slice(NUMBER_ZERO, NUMBER_FIVE);
        }
    });
    const [errorPreCode, setErrorPreCode] = useState(false);
    const [preOccupation, setPreOccupation] = useState(() => {
        if (isEmploymentTypeRetired) {
            return '';
        } else {
            return safeString(preEmployer?.Occupation);
        }
    });
    const [errorPreOcuppation, setErrorPreOcuppation] = useState(false);
    const [preJobYear, setPreJobYear] = useState(() => {
        if (isEmploymentTypeRetired) {
            return '';
        } else {
            return (
                initialPrimaryApplicant?.PreviousEmployer?.TimeAtJob?.Years ??
                ZERO
            );
        }
    });
    const [errorPreJobYear, setErrorPreJobYear] = useState(false);
    const [preJobMonth, setPreJobMonth] = useState(() => {
        if (isEmploymentTypeRetired) {
            return '';
        } else {
            return (
                initialPrimaryApplicant?.PreviousEmployer?.TimeAtJob?.Months ??
                ZERO
            );
        }
    });
    const [errorPreJobMonth, setErrorPreJobMonth] = useState(false);

    useEffect(() => {
        if (!selfEmployed && retired) {
            if (company === '') setCompany('  ');
            if (address === '') setAddress(' ');
            if (code === '') setCode(' ');
            if (occupation === '') setOccupation(' ');
        }
    }, [retired]);

    useEffect(() => {
        if (selfEmployed || retired) setEmployed(false);
        else setEmployed(true);
    }, [retired, selfEmployed]);

    const updateStateOptions = (
        isCanada: boolean,
        setOptions: (options: any) => void,
        setProvinceFn: (province: string) => void,
    ) => {
        setOptions(getStateOptions(isCanada, occupationIndividualInformation));
        setProvinceFn(isCanada ? 'AB' : 'AK');
    };

    useEffect(() => {
        updateStateOptions(country === 'Canada', setStateOptions, setProvince);
    }, [country]);

    useEffect(() => {
        updateStateOptions(
            preCountry === 'Canada',
            setPreStateOptions,
            setPreProvince,
        );
    }, [preCountry]);

    const getAllStateAndFunc = () => ({
        retired,
        employed,
        selfEmployed,
        city,
        country,
        province,
        code,
        occupation,
        jobYear,
        jobMonth,
        address,
        addressSecondary,
        company,
        setErrorCompany,
        setErrorAddress,
        setErrorAddressSecondary,
        setErrorCity,
        setErrorCode,
        setErrorOcuppation,
        setErrorJobYear,
        setErrorJobMonth,
        preCity,
        preCountry,
        preProvince,
        preCode,
        preOccupation,
        preJobYear,
        preJobMonth,
        preAddress,
        preAddressSecondary,
        preCompany,
        setErrorPreCompany,
        setErrorPreAddress,
        setErrorPreAddressSecondary,
        setErrorPreCity,
        setErrorPreCode,
        setErrorPreOcuppation,
        setErrorPreJobYear,
        setErrorPreJobMonth,
    });

    useEffect(() => {
        if (handlerChange) {
            handlerChange(getAllStateAndFunc());
        }
    }, [
        retired,
        employed,
        selfEmployed,
        city,
        country,
        province,
        code,
        occupation,
        jobYear,
        jobMonth,
        address,
        addressSecondary,
        company,
        preCity,
        preCountry,
        preProvince,
        preCode,
        preOccupation,
        preJobYear,
        preJobMonth,
        preAddress,
        preAddressSecondary,
        preCompany,
    ]);

    //for edit mode
    useEffect(() => {
        if (!initialPrimaryApplicant) {
            if (isEditMode && handlerChange) {
                setFieldValueUpdated(true);
            }
            return;
        }
        const getProvince = (employer: any) => {
            const state = employer?.Address?.State ?? '';
            const country = employer?.Address?.Country;
            const isCanada = country === 'Canada' || country === 'CA';

            const matchedState = getStateOptions(
                isCanada,
                occupationIndividualInformation,
            ).find((item) => item.value === state);

            return matchedState?.value ?? '';
        };
        const state = getProvince(initialPrimaryApplicant?.CurrentEmployer);
        setProvince(state);
        const preState = getProvince(initialPrimaryApplicant?.PreviousEmployer);
        setPreProvince(preState);
        if (isEditMode && handlerChange) {
            const changeData = {
                ...getAllStateAndFunc(),
                province: state,
                preProvince: preState,
            };
            handlerChange(changeData);
            setFieldValueUpdated(true);
        }
    }, []);

    const optionsEmployedType = [
        {
            value: 'selfemployed',
            label: occupationIndividualInformation.SELF_EMPLOYED,
        },
        {
            value: 'retired',
            label: occupationIndividualInformation.RETIRED,
        },
    ];

    const getFormattedCode = (
        value: any,
        currentStateVal: string,
        postalCountry: string,
    ) => {
        let postalCode = value.trim().toUpperCase();
        if (postalCode.length > currentStateVal.length) {
            if (postalCountry === 'USA') {
                postalCode = formatPostalCode_US(postalCode);
            } else {
                postalCode = formatPostalCode_CA(postalCode);
            }
        }
        return postalCode;
    };

    const handlePostalCodeChange = (
        event: any,
        currentValue: string,
        callback: any,
        postalCountry: string,
    ) => {
        const formattedCode = getFormattedCode(
            event.target.value,
            currentValue,
            postalCountry,
        );
        callback(formattedCode);
    };

    const handleMonthChange = (value: string) => {
        // eslint-disable-next-line no-magic-numbers
        const digits = value.replace(/\D/g, '').slice(0, 2);

        const num = Number(digits);
        if (num > MAX_MONTH) {
            return digits[0];
        }

        return removeLeadingZeros(digits);
    };

    return (
        <div className={'company'}>
            <div className='o-fieldset__row mb-0'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/1  u-1/1@xs '>
                        <p>
                            <strong>
                                {
                                    occupationIndividualInformation.CURRENT_EMPLOYER
                                }
                            </strong>
                        </p>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/1  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.EMPLOY_INFO}
                            </label>
                            <div className={'flex'}>
                                <div className='c-form-field__box'>
                                    <Checkbox
                                        name={optionsEmployedType[0].label}
                                        value={'selfemployed'}
                                        onCheck={(val: any) => {
                                            setSelfEmployed(val);
                                            markCreditFormTouched();
                                        }}
                                        checked={selfEmployed}
                                        allBlack={true}
                                    />
                                </div>
                                <div className='c-form-field__box u-ml-small'>
                                    <Checkbox
                                        name={optionsEmployedType[1].label}
                                        value={'retired'}
                                        onCheck={(val: any) => {
                                            setRetired(val);
                                            markCreditFormTouched();
                                        }}
                                        checked={retired}
                                        allBlack={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {selfEmployed
                                    ? occupationIndividualInformation.COMPANY
                                    : occupationIndividualInformation.EMPLOYER}
                                {!selfEmployed && retired ? '' : '*'}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input ${
                                            errorCompany ? 'is-error' : ''
                                        }`}
                                        type='text'
                                        placeholder=''
                                        value={company}
                                        maxLength={50}
                                        onChange={(event) => {
                                            setCompany(event.target.value);
                                            markCreditFormTouched();
                                        }}
                                        onBlur={() =>
                                            setCompany(company.trim())
                                        }
                                    />
                                </div>
                                <div>
                                    {errorCompany ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {
                                                occupationIndividualInformation.VALID_REQUIRED_ERR
                                            }
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.ADDRESS} 1
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input ${
                                            errorAddress ? 'is-error' : ''
                                        }`}
                                        type='text'
                                        placeholder=''
                                        maxLength={40}
                                        value={address.toString()}
                                        onChange={(event) => {
                                            setAddress(event.target.value);
                                            markCreditFormTouched();
                                        }}
                                    />
                                </div>
                                <div>
                                    {errorAddress ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {
                                                occupationIndividualInformation.ADDRESS_ERR
                                            }
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.ADDRESS} 2
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={'c-input__input'}
                                        type='text'
                                        placeholder=''
                                        value={addressSecondary.toString()}
                                        maxLength={25}
                                        onChange={(event) => {
                                            setAddressSecondary(
                                                event.target.value,
                                            );
                                            markCreditFormTouched();
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.CITY}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input ${
                                            errorCity ? 'is-error' : ''
                                        }`}
                                        type='text'
                                        placeholder=''
                                        maxLength={30}
                                        value={city.toString()}
                                        onChange={(event) => {
                                            setCity(event.target.value);
                                            markCreditFormTouched();
                                        }}
                                        onBlur={() => setCity(city.trim())}
                                    />
                                </div>
                                <div>
                                    {errorCity ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {
                                                occupationIndividualInformation.CITY_ERR
                                            }
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {country !== 'USA'
                                    ? occupationIndividualInformation.PROVINCE
                                    : occupationIndividualInformation.STATE}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <ComboBox
                                        name={'optionProvince'}
                                        options={stateOptions}
                                        defaultValue={province}
                                        placeholder={''}
                                        selectedOption={province}
                                        onSelect={(value) => {
                                            setProvince(value);
                                            markCreditFormTouched();
                                        }}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {country !== 'USA'
                                    ? occupationIndividualInformation.POSTAL
                                    : occupationIndividualInformation.ZIP}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input ${
                                            errorCode ? 'is-error' : ''
                                        }`}
                                        type='text'
                                        placeholder=''
                                        value={code.toString()}
                                        onChange={(e) => {
                                            handlePostalCodeChange(
                                                e,
                                                code,
                                                setCode,
                                                country,
                                            );
                                            markCreditFormTouched();
                                        }}
                                        maxLength={
                                            country !== 'USA'
                                                ? MAXZIPCA
                                                : MAXZIPUS
                                        }
                                    />
                                </div>
                                <div>
                                    {errorCode ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {country !== 'USA'
                                                ? occupationIndividualInformation.POSTAL_ERR
                                                : occupationIndividualInformation.ZIP_ERR}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.COUNTRY}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <ComboBox
                                        name={'optionOne'}
                                        options={[
                                            { value: 'USA', label: 'USA' },
                                            {
                                                value: 'Canada',
                                                label: 'Canada',
                                            },
                                        ]}
                                        defaultValue={'USA'}
                                        placeholder={''}
                                        selectedOption={country}
                                        onSelect={(value) => {
                                            setCountry(value);
                                            markCreditFormTouched();
                                        }}
                                        disabled={isDisabled}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.OCCUPATION}
                                {!selfEmployed && retired ? '' : '*'}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input ${
                                            errorOcuppation ? 'is-error' : ''
                                        }`}
                                        type='text'
                                        placeholder=''
                                        value={occupation.toString()}
                                        maxLength={20}
                                        onChange={(event) => {
                                            setOccupation(event.target.value);
                                            markCreditFormTouched();
                                        }}
                                        onBlur={() =>
                                            setOccupation(occupation.trim())
                                        }
                                    />
                                </div>
                                <div>
                                    {errorOcuppation ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {
                                                occupationIndividualInformation.OCCUPATION_ERR
                                            }
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.YEARS}
                                {!selfEmployed && retired ? '' : '*'}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input current-employer-year ${
                                            errorJobYear ? 'is-error' : ''
                                        }`}
                                        type='number'
                                        name='jobYear'
                                        placeholder=''
                                        value={jobYear?.toString()}
                                        onChange={(event) => {
                                            const inputValue =
                                                event.target.value
                                                    .replace(/\D/g, '')
                                                    // eslint-disable-next-line no-magic-numbers
                                                    .slice(0, 2);
                                            setJobYear(
                                                removeLeadingZeros(inputValue),
                                            );
                                            markCreditFormTouched();
                                        }}
                                        onKeyDown={handleKeyDownYearMonth}
                                        min={0}
                                        max={99}
                                    />
                                </div>
                                <div>
                                    {errorJobYear ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {
                                                occupationIndividualInformation.YEARS_ERR
                                            }
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {occupationIndividualInformation.MONTHS}
                                {!selfEmployed && retired ? '' : '*'}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <input
                                        className={`c-input__input ${
                                            errorJobMonth ? 'is-error' : ''
                                        }`}
                                        type='number'
                                        name='jobMonth'
                                        placeholder=''
                                        value={jobMonth?.toString()}
                                        onChange={(event) => {
                                            setJobMonth(
                                                handleMonthChange(
                                                    event.target.value,
                                                ),
                                            );
                                            markCreditFormTouched();
                                        }}
                                        onKeyDown={handleKeyDownYearMonth}
                                        min={0}
                                        max={11}
                                    />
                                </div>
                                <div>
                                    {errorJobMonth ? (
                                        <span
                                            className={'c-error-message'}
                                            style={{
                                                position: 'absolute',
                                            }}
                                        >
                                            {
                                                occupationIndividualInformation.MONTHS_ERR
                                            }
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {typeof jobYear === 'number' && jobYear < NUMBER_TWO && (
                <div className='previous-employer'>
                    <hr className='mv-50' />
                    <div className='o-fieldset__row mb-0'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/1  u-1/1@xs '>
                                <p className='mb-10'>
                                    <strong>
                                        {
                                            occupationIndividualInformation.PREVIOUS_EMPLOYER
                                        }
                                    </strong>
                                </p>
                                <p className='mb-32'>
                                    {
                                        occupationIndividualInformation.PREVIOUS_EMPLOYER_DESC1
                                    }
                                    <br />
                                    {
                                        occupationIndividualInformation.PREVIOUS_EMPLOYER_DESC2
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {selfEmployed
                                            ? occupationIndividualInformation.COMPANY
                                            : occupationIndividualInformation.EMPLOYER}
                                        *
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreCompany
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                placeholder=''
                                                value={preCompany}
                                                maxLength={30}
                                                onChange={(event) => {
                                                    setPreCompany(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                onBlur={() =>
                                                    setPreCompany(
                                                        preCompany.trim(),
                                                    )
                                                }
                                            />
                                        </div>
                                        <div>
                                            {errorPreCompany ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        occupationIndividualInformation.VALID_REQUIRED_ERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {
                                            occupationIndividualInformation.ADDRESS
                                        }{' '}
                                        1
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreAddress
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                placeholder=''
                                                maxLength={40}
                                                value={preAddress.toString()}
                                                onChange={(event) => {
                                                    setPreAddress(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                            />
                                        </div>
                                        <div>
                                            {errorPreAddress ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        occupationIndividualInformation.ADDRESS_ERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {
                                            occupationIndividualInformation.ADDRESS
                                        }{' '}
                                        2
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={'c-input__input'}
                                                type='text'
                                                placeholder=''
                                                value={preAddressSecondary.toString()}
                                                maxLength={25}
                                                onChange={(event) => {
                                                    setPreAddressSecondary(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/2  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {occupationIndividualInformation.CITY}
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreCity
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                placeholder=''
                                                maxLength={30}
                                                value={preCity.toString()}
                                                onChange={(event) => {
                                                    setPreCity(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                onBlur={() =>
                                                    setPreCity(preCity.trim())
                                                }
                                            />
                                        </div>
                                        <div>
                                            {errorPreCity ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        occupationIndividualInformation.CITY_ERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {preCountry !== 'USA'
                                            ? occupationIndividualInformation.PROVINCE
                                            : occupationIndividualInformation.STATE}
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <ComboBox
                                                name={'optionProvince'}
                                                options={preStateOptions}
                                                defaultValue={preProvince}
                                                placeholder={''}
                                                selectedOption={preProvince}
                                                onSelect={(value) => {
                                                    setPreProvince(value);
                                                    markCreditFormTouched();
                                                }}
                                                disabled={isDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {preCountry !== 'USA'
                                            ? occupationIndividualInformation.POSTAL
                                            : occupationIndividualInformation.ZIP}
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreCode
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                placeholder=''
                                                value={preCode.toString()}
                                                onChange={(e) => {
                                                    handlePostalCodeChange(
                                                        e,
                                                        preCode,
                                                        setPreCode,
                                                        preCountry,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                maxLength={
                                                    preCountry !== 'USA'
                                                        ? MAXZIPCA
                                                        : MAXZIPUS
                                                }
                                            />
                                        </div>
                                        <div>
                                            {errorPreCode ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {preCountry !== 'USA'
                                                        ? occupationIndividualInformation.POSTAL_ERR
                                                        : occupationIndividualInformation.ZIP_ERR}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {
                                            occupationIndividualInformation.COUNTRY
                                        }
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <ComboBox
                                                name={'optionOne'}
                                                options={[
                                                    {
                                                        value: 'USA',
                                                        label: 'USA',
                                                    },
                                                    {
                                                        value: 'Canada',
                                                        label: 'Canada',
                                                    },
                                                ]}
                                                defaultValue={'USA'}
                                                placeholder={''}
                                                selectedOption={preCountry}
                                                onSelect={(value) => {
                                                    setPreCountry(value);
                                                    markCreditFormTouched();
                                                }}
                                                disabled={isDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-fieldset__row'>
                        <div className='o-layout'>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {
                                            occupationIndividualInformation.OCCUPATION
                                        }
                                        *
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreOcuppation
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='text'
                                                placeholder=''
                                                value={preOccupation.toString()}
                                                maxLength={20}
                                                onChange={(event) => {
                                                    setPreOccupation(
                                                        event.target.value,
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                onBlur={() =>
                                                    setPreOccupation(
                                                        preOccupation.trim(),
                                                    )
                                                }
                                            />
                                        </div>
                                        <div>
                                            {errorPreOcuppation ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        occupationIndividualInformation.OCCUPATION_ERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {occupationIndividualInformation.YEARS}*
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreJobYear
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='number'
                                                name='prevJobYear'
                                                placeholder=''
                                                value={preJobYear.toString()}
                                                onChange={(event) => {
                                                    const inputValue =
                                                        event.target.value
                                                            .replace(/\D/g, '')
                                                            // eslint-disable-next-line no-magic-numbers
                                                            .slice(0, 2);
                                                    setPreJobYear(
                                                        removeLeadingZeros(
                                                            inputValue,
                                                        ),
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                min={0}
                                                max={99}
                                                onKeyDown={
                                                    handleKeyDownYearMonth
                                                }
                                            />
                                        </div>
                                        <div>
                                            {errorPreJobYear ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        occupationIndividualInformation.YEARS_ERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='o-layout__item  u-1/3  u-1/1@xs'>
                                <div className='c-form-field'>
                                    <label
                                        htmlFor='m1l7e063'
                                        className='c-form-field__label'
                                    >
                                        {occupationIndividualInformation.MONTHS}
                                        *
                                    </label>
                                    <div className='c-form-field__box'>
                                        <div className='c-input'>
                                            <input
                                                className={`c-input__input ${
                                                    errorPreJobMonth
                                                        ? 'is-error'
                                                        : ''
                                                }`}
                                                type='number'
                                                name='prevJobMonth'
                                                placeholder=''
                                                value={preJobMonth.toString()}
                                                onChange={(event) => {
                                                    setPreJobMonth(
                                                        handleMonthChange(
                                                            event.target.value,
                                                        ),
                                                    );
                                                    markCreditFormTouched();
                                                }}
                                                min={0}
                                                max={11}
                                                onKeyDown={
                                                    handleKeyDownYearMonth
                                                }
                                            />
                                        </div>
                                        <div>
                                            {errorPreJobMonth ? (
                                                <span
                                                    className={
                                                        'c-error-message'
                                                    }
                                                    style={{
                                                        position: 'absolute',
                                                    }}
                                                >
                                                    {
                                                        occupationIndividualInformation.MONTHS_ERR
                                                    }
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Company;
